import { useCallback, useEffect, useRef, useState } from "react";
import {
  getBranch,
  getSegments,
  getAllSuppliers,
  getAssets,
  getDrivers,
  unwrapList,
  getFleetGroupsWithAssets,
  syncFleetGroups,
  getTags,
  getAssetRio,
  getMarketplaceVehicles,
  IMarketplaceVehicle,
} from "../../../services/form";
import { DashboardFilterOption } from "../../../types/dashboard";
import { IFleetGroupsResponse, IFleetGroupWithAssets } from "../../../types/fleetGroups";
import {
  useGetStoredSelectedBranches,
  useGetStoredSelectedSegments,
  useGetStoredSelectedFleetGroups,
  useGetStoredSelectedClients,
  useGetStoredSelectedSuppliers,
  useGetStoredSelectedVehicles,
  useGetStoredSelectedDrivers,
  useGetStoredIncludeNullBranch,
  useGetStoredIncludeNullSegment,
  useGetStoredIncludeNullClient,
  useGetStoredIncludeNullSupplier,
  useGetStoredIncludeNullVehicle,
  useGetStoredIncludeNullDriver,
  useGetStoredKnownOptionIds,
  setSelectedBranches,
  setSelectedSegments,
  setSelectedFleetGroups,
  setSelectedClients,
  setSelectedSuppliers,
  setSelectedVehicles,
  setSelectedDrivers,
  setIncludeNullBranch,
  setIncludeNullSegment,
  setIncludeNullClient,
  setIncludeNullSupplier,
  setIncludeNullVehicle,
  setIncludeNullDriver,
  setAssets as setStoredAssets,
  setTreeReady,
  setKnownOptionIds,
} from "@store/tmsAssetTree/tmsAssetTreeSlice";
import { useAppDispatch } from "@store";

export interface IFilter {
  id: string;
  label: string;
}

export interface IFilterOptions {
  branches: IFilter[];
  segments: IFilter[];
  fleetGroups: IFilter[];
  clients: IFilter[];
  suppliers: IFilter[];
  vehicles: IFilter[];
  drivers: IFilter[];
}

export interface Tag {
  id: string;
  type: string;
  name: string;
  account_id: string;
}

/**
 * Estrutura de veículo processada com informações de frota
 */
export interface IProcessedVehicle {
  id: string;
  name: string;
  licensePlate: string | null;
  fleetGroupId: string | null;
  fleetGroupName: string | null;
}

/**
 * Extrai o grupo de frota do nome do asset.
 * Padrões encontrados:
 *   - "SWC5E25 - V5180G - BOIADEIRO" (3 partes = grupo BOIADEIRO, asset V5180G)
 *   - "SQY2B46 - Butiá" (2 partes = grupo Butiá, asset SQY2B46)
 *   - "SVA9F22" (1 parte = Sem grupo, exibe solto)
 * Retorna "Sem grupo" se não encontrar o padrão.
 */
export const extractFleetGroup = (assetName: string): string => {
  if (!assetName || typeof assetName !== "string") {
    return "Sem grupo";
  }

  // Normaliza separadores: substitui variações de " - ", "- ", " -", "-" por um separador padrão
  const normalized = assetName.replaceAll("-", "|");
  const parts = normalized
    .split("|")
    .map((p) => p.trim())
    .filter(Boolean);

  if (parts.length >= 3) {
    return parts.at(-1) as string; // Último elemento é o grupo
  }

  if (parts.length === 2) {
    return parts[1]; // Segunda parte é o grupo (ex: "SQY2B46 - Butiá" → Butiá)
  }

  return "Sem grupo"; // Só tem placa/VIN
};

/**
 * Extrai o código/identificador do asset do nome.
 * Padrões:
 *   - "SWC5E25 - V5180G - BOIADEIRO" (3 partes) → retorna V5180G (código)
 *   - "SQY2B46 - Butiá" (2 partes) → retorna SQY2B46 (placa)
 *   - "SVA9F22" (1 parte) → retorna SVA9F22 (nome completo)
 */
export const extractAssetCode = (assetName: string): string => {
  if (!assetName || typeof assetName !== "string") {
    return assetName || "";
  }

  // Normaliza separadores
  const normalized = assetName.replaceAll("-", "|");
  const parts = normalized
    .split("|")
    .map((p) => p.trim())
    .filter(Boolean);

  if (parts.length >= 3) {
    return parts[1]; // Segunda parte é o código (ex: V5180G)
  }

  if (parts.length === 2) {
    return parts[0]; // Primeira parte é a placa (ex: SQY2B46)
  }

  return assetName; // Retorna nome completo se não tiver o padrão
};

/**
 * Extrai grupos de frota únicos de uma lista de veículos
 */
export const extractUniqueFleetGroups = (vehicles: Array<Record<string, unknown>>): IFilter[] => {
  const groupSet = new Set<string>();

  vehicles.forEach((vehicle) => {
    const name =
      (vehicle.name as string | undefined) ??
      (vehicle.licensePlate as string | undefined) ??
      (vehicle.license_plate as string | undefined) ??
      "";
    const group = extractFleetGroup(name);
    groupSet.add(group);
  });

  return Array.from(groupSet)
    .sort((a, b) => {
      if (a === "Sem grupo") return 1;
      if (b === "Sem grupo") return -1;
      return a.localeCompare(b);
    })
    .map((group) => ({
      id: group,
      label: group,
    }));
};

export const useAssetTree = () => {
  const selectedBranches = useGetStoredSelectedBranches();
  const selectedSegments = useGetStoredSelectedSegments();
  const selectedFleetGroups = useGetStoredSelectedFleetGroups();
  const selectedClients = useGetStoredSelectedClients();
  const selectedSuppliers = useGetStoredSelectedSuppliers();
  const selectedVehicles = useGetStoredSelectedVehicles();
  const selectedDrivers = useGetStoredSelectedDrivers();
  const includeNullBranch = useGetStoredIncludeNullBranch();
  const includeNullSegment = useGetStoredIncludeNullSegment();
  const includeNullClient = useGetStoredIncludeNullClient();
  const includeNullSupplier = useGetStoredIncludeNullSupplier();
  const includeNullVehicle = useGetStoredIncludeNullVehicle();
  const includeNullDriver = useGetStoredIncludeNullDriver();
  const knownOptionIds = useGetStoredKnownOptionIds();

  const [filterOptions, setFilterOptions] = useState<IFilterOptions>({
    branches: [],
    segments: [],
    fleetGroups: [],
    clients: [],
    suppliers: [],
    vehicles: [],
    drivers: [],
  });

  // Estado para dados do novo endpoint de fleet groups
  const [fleetGroupsData, setFleetGroupsData] = useState<IFleetGroupsResponse | null>(null);
  const [processedVehiclesFromApi, setProcessedVehiclesFromApi] = useState<IProcessedVehicle[]>([]);
  const [isLoadingFleetGroups, setIsLoadingFleetGroups] = useState(false);
  const [fleetGroupsError, setFleetGroupsError] = useState<string | null>(null);
  const [tags, setTags] = useState<Tag[]>([]);
  const [assets, setAssets] = useState<any[]>([]);
  const [isFilterOptionsSettled, setIsFilterOptionsSettled] = useState(false);
  const [isMarketplaceVehiclesSettled, setIsMarketplaceVehiclesSettled] = useState(false);

  const getVehicleLabel = (vehicle: Record<string, unknown>): string | undefined => {
    return (
      (vehicle.name as string | undefined) ??
      (vehicle.licensePlate as string | undefined) ??
      (vehicle.license_plate as string | undefined) ??
      (vehicle.model as string | undefined) ??
      (vehicle.displayName as string | undefined)
    );
  };

  const toFilterOptions = (
    items: unknown,
    getLabel: (item: Record<string, unknown>) => string | undefined
  ): DashboardFilterOption[] => {
    if (!Array.isArray(items)) {
      return [];
    }

    return items.reduce<DashboardFilterOption[]>((acc, rawItem) => {
      const item = rawItem as Record<string, unknown>;
      const label = getLabel(item);
      if (!label) {
        return acc;
      }

      const idCandidate = (item.id ??
        item.value ??
        item.uuid ??
        item.name ??
        item.licensePlate ??
        item.license_plate ??
        label) as string | number;

      acc.push({ id: String(idCandidate), label });
      return acc;
    }, []);
  };

  const getSupplierClientType = (item: Record<string, unknown>): string => {
    const rawType = (item.type ?? item.clientType ?? item.tipo) as string | number | undefined;
    return String(rawType ?? "")
      .trim()
      .toUpperCase();
  };

  const splitClientsByType = (items: Array<Record<string, unknown>>) => {
    const clients: Array<Record<string, unknown>> = [];
    const suppliers: Array<Record<string, unknown>> = [];

    items.forEach((item) => {
      const type = getSupplierClientType(item);
      if (type === "FORNECEDOR") {
        suppliers.push(item);
      } else {
        clients.push(item);
      }
    });

    return { clients, suppliers };
  };

  /**
   * Busca dados do novo endpoint /fleet-groups
   * Processa e organiza veículos por frota
   */
  const fetchFleetGroupsData = useCallback(async () => {
    setIsLoadingFleetGroups(true);
    setFleetGroupsError(null);

    try {
      const response = await getFleetGroupsWithAssets();
      setFleetGroupsData(response as IFleetGroupsResponse);

      // Processa veículos do response para estrutura flat
      const processed: IProcessedVehicle[] = [];
      const fleetGroupsResponse = response as IFleetGroupsResponse;

      if (fleetGroupsResponse?.fleet_groups) {
        fleetGroupsResponse.fleet_groups.forEach((group: IFleetGroupWithAssets) => {
          group.assets.forEach((asset) => {
            processed.push({
              id: asset.asset_id,
              name: asset.asset_name || asset.license_plate || asset.asset_id,
              licensePlate: asset.license_plate,
              fleetGroupId: group.fleet_group_id,
              fleetGroupName: group.fleet_group_name,
            });
          });
        });
      }

      setProcessedVehiclesFromApi(processed);
      console.log(`[useAssetTree] Loaded ${processed.length} vehicles from fleet-groups API`);
    } catch (error) {
      console.error("[useAssetTree] Error fetching fleet groups:", error);
      setFleetGroupsError("Erro ao carregar grupos de frota");
    } finally {
      setIsLoadingFleetGroups(false);
    }
  }, []);

  /**
   * Dispara sincronização com RIO API
   */
  const triggerSync = useCallback(async () => {
    setIsLoadingFleetGroups(true);
    try {
      await syncFleetGroups();
      // Recarrega dados após sync
      await fetchFleetGroupsData();
      console.log("[useAssetTree] Fleet groups synced successfully");
    } catch (error) {
      console.error("[useAssetTree] Error syncing fleet groups:", error);
      setFleetGroupsError("Erro ao sincronizar grupos de frota");
    } finally {
      setIsLoadingFleetGroups(false);
    }
  }, [fetchFleetGroupsData]);

  const fetchFilterOptions = useCallback(async () => {
    try {
      // Busca dados em paralelo - incluindo fleet groups do novo endpoint
      const [branches, segments, clients, vehicles, drivers] = await Promise.all([
        getBranch(),
        getSegments(),
        getAllSuppliers(),
        getAssets(),
        getDrivers(),
      ]);

      // Também busca fleet groups do novo endpoint (em paralelo)
      fetchFleetGroupsData();

      const clientsUnwrap = unwrapList(clients);
      const { clients: clientsOnly, suppliers: suppliersOnly } = splitClientsByType(clientsUnwrap);
      const vehiclesUnwrap = unwrapList(vehicles);

      // Extrai grupos de frota dos nomes dos veículos (fallback legado)
      // Padrão: "PLACA - CÓDIGO - GRUPO" (ex: "SWC5E25 - V5180G - BOIADEIRO")
      const extractedFleetGroups = extractUniqueFleetGroups(vehiclesUnwrap);

      setFilterOptions({
        branches: toFilterOptions(branches, (branch) => branch.name as string | undefined),
        segments: toFilterOptions(segments, (segment) => segment.name as string | undefined),
        fleetGroups: extractedFleetGroups,
        clients: toFilterOptions(clientsOnly, (client) => client.name as string | undefined),
        suppliers: toFilterOptions(suppliersOnly, (supplier) => supplier.name as string | undefined),
        vehicles: toFilterOptions(vehiclesUnwrap, getVehicleLabel),
        drivers: toFilterOptions(drivers, (driver) => driver.name as string | undefined),
      });
    } catch (error) {
      console.error("Error fetching Trucker|Pay Gestor filter options:", error);
    } finally {
      setIsFilterOptionsSettled(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchFleetGroupsData]);

  useEffect(() => {
    fetchFilterOptions();
  }, [fetchFilterOptions]);

  const dispatch = useAppDispatch();

  const fetchMarketplaceVehicles = async () => {
    try {
      const [tagResponse, rioAssetsResponse, vehicles] = await Promise.all([
        getTags(),
        getAssetRio(),
        getMarketplaceVehicles(),
      ]);

      setTags(tagResponse.items ?? []);

      const rioAssetMap: Record<string, any> = {};
      for (const rioAsset of rioAssetsResponse.items ?? []) {
        rioAssetMap[rioAsset.id] = rioAsset;
      }

      const adapted = vehicles.map((vehicle: IMarketplaceVehicle) => {
        const rioAsset = rioAssetMap[vehicle.rioAssetId];
        return {
          id: vehicle.rioAssetId,
          name: vehicle.name || vehicle.licensePlate,
          type: vehicle.vehicleType,
          _embedded: rioAsset?._embedded ?? { tags: { items: [] } },
        };
      });

      setAssets(adapted);
      dispatch(setStoredAssets(adapted));
    } catch {
      setTags([]);
      setAssets([]);
      dispatch(setStoredAssets([]));
    } finally {
      setIsMarketplaceVehiclesSettled(true);
    }
  };

  useEffect(() => {
    fetchMarketplaceVehicles();
  }, []);

  // Seleciona todos os itens por padrão na primeira carga (aguarda filterOptions e assets terminarem, com ou sem dados)
  const initialSelectionDone = useRef(false);

  useEffect(() => {
    if (initialSelectionDone.current) return;

    // Aguarda as duas fontes terminarem (sucesso ou erro) antes de decidir a seleção
    // inicial. Os veículos vêm de uma API separada (marketplace) e podem falhar ou
    // vir vazios sem que isso trave os demais filtros (branch/segment/client/etc).
    if (!isFilterOptionsSettled || !isMarketplaceVehiclesSettled) return;

    const { branches, segments, clients, suppliers, drivers } = filterOptions;

    // Só auto-seleciona se nada estiver selecionado (primeira carga)
    const hasAnySelection =
      selectedBranches.length ||
      selectedSegments.length ||
      selectedClients.length ||
      selectedSuppliers.length ||
      selectedDrivers.length ||
      selectedVehicles.length;

    const currentOptionIds = {
      branches: branches.map((b) => b.id),
      segments: segments.map((s) => s.id),
      clients: clients.map((c) => c.id),
      suppliers: suppliers.map((s) => s.id),
      vehicles: assets.map((a: any) => String(a.id)),
      drivers: drivers.map((d) => d.id),
    };

    if (hasAnySelection) {
      if (knownOptionIds) {
        const addNewOptions = (
          selected: string[],
          known: string[],
          current: string[],
          setSelected: (items: string[]) => { type: string }
        ) => {
          const selectedSet = new Set(selected);
          if (!known.every((id) => selectedSet.has(id))) return;
          const newIds = current.filter((id) => !known.includes(id) && !selectedSet.has(id));
          if (newIds.length) dispatch(setSelected([...selected, ...newIds]));
        };

        addNewOptions(selectedBranches, knownOptionIds.branches, currentOptionIds.branches, setSelectedBranches);
        addNewOptions(selectedSegments, knownOptionIds.segments, currentOptionIds.segments, setSelectedSegments);
        addNewOptions(selectedClients, knownOptionIds.clients, currentOptionIds.clients, setSelectedClients);
        addNewOptions(selectedSuppliers, knownOptionIds.suppliers, currentOptionIds.suppliers, setSelectedSuppliers);
        addNewOptions(selectedVehicles, knownOptionIds.vehicles, currentOptionIds.vehicles, setSelectedVehicles);
        addNewOptions(selectedDrivers, knownOptionIds.drivers, currentOptionIds.drivers, setSelectedDrivers);
      }

      dispatch(setKnownOptionIds(currentOptionIds));
      initialSelectionDone.current = true;
      dispatch(setTreeReady(true));
      return;
    }

    dispatch(setSelectedBranches(currentOptionIds.branches));
    dispatch(setSelectedSegments(currentOptionIds.segments));
    dispatch(setSelectedClients(currentOptionIds.clients));
    dispatch(setSelectedSuppliers(currentOptionIds.suppliers));
    dispatch(setSelectedDrivers(currentOptionIds.drivers));
    dispatch(setSelectedVehicles(currentOptionIds.vehicles));
    dispatch(setKnownOptionIds(currentOptionIds));
    dispatch(setIncludeNullBranch(true));
    dispatch(setIncludeNullSegment(true));
    dispatch(setIncludeNullClient(true));
    dispatch(setIncludeNullSupplier(true));
    dispatch(setIncludeNullVehicle(true));
    dispatch(setIncludeNullDriver(true));
    dispatch(setTreeReady(true));

    initialSelectionDone.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterOptions, assets, isFilterOptionsSettled, isMarketplaceVehiclesSettled]);

  return {
    selectedBranches,
    selectedSegments,
    selectedFleetGroups,
    selectedClients,
    selectedSuppliers,
    selectedVehicles,
    selectedDrivers,
    includeNullBranch,
    includeNullSegment,
    includeNullClient,
    includeNullSupplier,
    includeNullVehicle,
    includeNullDriver,
    filterOptions,
    setSelectedBranches,
    setSelectedSegments,
    setSelectedFleetGroups,
    setSelectedClients,
    setSelectedSuppliers,
    setSelectedVehicles,
    setSelectedDrivers,
    setIncludeNullBranch,
    setIncludeNullSegment,
    setIncludeNullClient,
    setIncludeNullSupplier,
    setIncludeNullVehicle,
    setIncludeNullDriver,
    // Novos dados do endpoint /fleet-groups
    fleetGroupsData,
    processedVehiclesFromApi,
    isLoadingFleetGroups,
    fleetGroupsError,
    triggerSync,
    fetchFleetGroupsData,
    tags,
    assets,
  };
};

export default useAssetTree;
